import React, { useState } from 'react';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  FormHelperText,
  Grid,
} from '@material-ui/core';
import { injectIntl, IntlShape } from 'react-intl';
import messages from 'containers/PreOpeningPage/messages';
import { translate, arrayTranslate } from 'containers/services/translate';
import EmptyAddFormBloc from 'components/EmptyAddFormBloc';
import {
  FormSubmitWrapper,
  FormContainer,
} from 'containers/PreOpeningPage/OpposingParty/styled-components';
import { Listings } from 'containers/AppContainer/typings';
import { VEHICULE } from 'containers/data-structure';
import { Autocomplete } from '@material-ui/lab';
import { createFilterOptions } from '@material-ui/lab/Autocomplete';
import { get, isEmpty, isNull } from 'lodash';
import {
  brand,
  filtredCompanies,
  getOptions,
  getByPlaceholderText,
  getFormContraints,
  validateRegistrationNumber,
  getInputLength,
  isAllInputsLengthValid,
  renderInputLengthErrorText,
  formatRegistrationNumber,
} from 'containers/PreOpeningPage/OpposingParty/utils';
import { SagisConfig } from 'containers/AppContainer/typings';
import validate from 'containers/services/validateForm';
import {
  displayErrorText,
  validateFormFields,
} from 'containers/services/validateForm';
import PrimaryButton from 'components/Buttons/PrimaryButton';
import SecondaryButton from 'components/Buttons/SecondaryButton';
import { FEATURES } from 'features/features';
import * as featureService from 'features/fflip-helpers';

export interface AddOpposingPartyProps {
  intl: IntlShape;
  listings: Listings;
  claimToPreopen: any;
  setClaimToPreopen: React.Dispatch<React.SetStateAction<any>>;
  setClaimRecap?: Function;
  toggleOpen: boolean;
  setToggleOpen: React.Dispatch<React.SetStateAction<boolean>>;
  renderSubForm: boolean;
  isForDrawer: boolean;
  opposingPartyData?: any;
  setOpposingPartyData?: Function;
}
const AddOpposingParty: React.FC<AddOpposingPartyProps> = ({
  listings,
  toggleOpen,
  setToggleOpen,
  claimToPreopen,
  setClaimToPreopen,
  renderSubForm,
  isForDrawer,
  ...props
}): JSX.Element => {
  const [showForm, setShowForm] = useState<Boolean>(false);
  const [isRegistrationNumberValid, setIsRegistrationNumberValid] = useState<
    Boolean
  >(true);
  const [
    registrationNumberErrorText,
    setRegistrationNumberErrorText,
  ] = useState<String | null>(null);
  const [inputsLengthErrorText, setInputsLengthErrorText] = React.useState<{
    [VEHICULE.POLICY_NUMBER_OPPOSING]: String;
    [VEHICULE.CERTIFICATE_NUMBER_OPPOSING]: String;
    [VEHICULE.CLAIM_NUMBER_OPPOSING]: String;
  }>({
    [VEHICULE.POLICY_NUMBER_OPPOSING]: '',
    [VEHICULE.CERTIFICATE_NUMBER_OPPOSING]: '',
    [VEHICULE.CLAIM_NUMBER_OPPOSING]: '',
  });
  const findNearestResponsability = () => {
    const responsabiltyList = get(listings, 'responsability', []);
    if (isEmpty(responsabiltyList)) {
      return undefined;
    }
    if (
      !isEmpty(responsabiltyList) &&
      !get(claimToPreopen, 'claim.responsibilityShare', null)
    ) {
      return null;
    }
    const responsabilityCalculated =
      100 - get(claimToPreopen, 'claim.responsibilityShare');

    let nearestResponsabilityRate = Number(
      responsabiltyList[1].ItemDescription,
    );
    let nearestResponsabilityKey = responsabiltyList[1].ItemKey;
    let minDifference = Math.abs(
      responsabilityCalculated - nearestResponsabilityRate,
    );

    for (let i = 2; i < responsabiltyList.length; i++) {
      const currentResponsability = Number(
        responsabiltyList[i].ItemDescription,
      );
      const currentDifference = Math.abs(
        responsabilityCalculated - currentResponsability,
      );

      if (currentDifference < minDifference) {
        minDifference = currentDifference;
        nearestResponsabilityRate = currentResponsability;
        nearestResponsabilityKey = responsabiltyList[i].ItemKey;
      }
    }

    return { nearestResponsabilityKey, nearestResponsabilityRate };
  };
  const currentResponsability = findNearestResponsability();
  const [formValues, setFormValues] = useState<any>({
    [VEHICULE.BRAND]: null,
    [VEHICULE.REGISTRATION_NUMBER]: null,
    [VEHICULE.OPPOSING_PARTY]: null,
    [VEHICULE.FIRST_NAME]: null,
    [VEHICULE.LAST_NAME]: null,
    [VEHICULE.POLICY_NUMBER_OPPOSING]: null,
    [VEHICULE.CERTIFICATE_NUMBER_OPPOSING]: null,
    [VEHICULE.CLAIM_NUMBER_OPPOSING]: null,
    [VEHICULE.REGISTRATION_NUMBER_TYPE]: null,
    [VEHICULE.RESPONSABILITY]: currentResponsability
      ? currentResponsability.nearestResponsabilityKey
      : null,
    [VEHICULE.RESPONSABILITY_RATE]: currentResponsability
      ? currentResponsability.nearestResponsabilityRate
      : null,
  });
  const hasMaterialDammage = claimToPreopen.opposingParty.hasMaterialDammage;
  const vehicules = featureService.hasFeature(FEATURES.RNVA)
    ? [
        ...claimToPreopen.opposingParty.vehicules,
        {
          ...formValues,
          [VEHICULE.REGISTRATION_NUMBER]: formatRegistrationNumber(
            formValues[VEHICULE.REGISTRATION_NUMBER],
            formValues[VEHICULE.REGISTRATION_NUMBER_TYPE],
          ),
        },
      ]
    : [...claimToPreopen.opposingParty.vehicules, formValues];
  const newVehicules = get(claimToPreopen, 'opposingParty.newVehicles', [])
    ? featureService.hasFeature(FEATURES.RNVA)
      ? [
          ...get(claimToPreopen, 'opposingParty.newVehicles', []),
          {
            ...formValues,
            [VEHICULE.REGISTRATION_NUMBER]: formatRegistrationNumber(
              formValues[VEHICULE.REGISTRATION_NUMBER],
              formValues[VEHICULE.REGISTRATION_NUMBER_TYPE],
            ),
          },
        ]
      : [...get(claimToPreopen, 'opposingParty.newVehicles', []), formValues]
    : [];

  const [formValidator, setFormValidator] = useState<any>({});

  const filterOptions = createFilterOptions({
    matchFrom: 'start',
    stringify: getOptions,
  });

  const handleCancel = () => {
    setShowForm(false);
    setFormValues({
      [VEHICULE.BRAND]: null,
      [VEHICULE.REGISTRATION_NUMBER]: null,
      [VEHICULE.OPPOSING_PARTY]: null,
      [VEHICULE.FIRST_NAME]: null,
      [VEHICULE.LAST_NAME]: null,
      [VEHICULE.POLICY_NUMBER_OPPOSING]: null,
      [VEHICULE.CERTIFICATE_NUMBER_OPPOSING]: null,
      [VEHICULE.CLAIM_NUMBER_OPPOSING]: null,
      [VEHICULE.REGISTRATION_NUMBER_TYPE]: null,
      [VEHICULE.RESPONSABILITY]: currentResponsability
        ? currentResponsability.nearestResponsabilityKey
        : null,
      [VEHICULE.RESPONSABILITY_RATE]: currentResponsability
        ? currentResponsability.nearestResponsabilityRate
        : null,
    });
  };

  const handleFormChange = (fieldName: string) => (
    event: React.ChangeEvent<{
      name?: string;
      value?: any;
    }>,
  ) => {
    if (event.target.name === VEHICULE.REGISTRATION_NUMBER) {
      if (featureService.hasFeature(FEATURES.RNVA)) {
        const error = validateRegistrationNumber(
          event.target.value,
          formValues[VEHICULE.REGISTRATION_NUMBER_TYPE],
          props,
        );
        setRegistrationNumberErrorText(error);
        setIsRegistrationNumberValid(!error);
      } else {
        const pattern = /^(?:\d{1,5}-[a-zA-Z]{1,2}-\d{1,2}|[a-zA-Z]-\d{1,9}|\d{1,2}-\d{1,6}|\d{1,5}-\d{1,2}|\d{1,5}-\d{1,2}-\d{1,2}|WW\d{1,6}|W18-\d{1,6})$/;
        setIsRegistrationNumberValid(pattern.test(event.target.value));
      }
    }

    if (
      event.target.name === VEHICULE.POLICY_NUMBER_OPPOSING ||
      event.target.name === VEHICULE.CERTIFICATE_NUMBER_OPPOSING ||
      event.target.name === VEHICULE.CLAIM_NUMBER_OPPOSING
    ) {
      const maxLength =
        event.target.name === VEHICULE.CERTIFICATE_NUMBER_OPPOSING ? 30 : 20;
      setInputsLengthErrorText({
        ...inputsLengthErrorText,
        [event.target.name]: renderInputLengthErrorText(
          3,
          maxLength,
          getInputLength(event.target.value),
          props,
        ),
      });
    }

    if (event.target.name === VEHICULE.RESPONSABILITY) {
      const selectedItem = get(listings, 'responsability', []).find(
        (el: any) => el.ItemKey === event.target.value,
      );
      return setFormValues(
        Object.assign(
          {},
          { ...formValues },
          {
            [fieldName]: event.target.value,
            [VEHICULE.RESPONSABILITY_RATE]: selectedItem?.ItemDescription,
          },
        ),
      );
    }

    return setFormValues(
      Object.assign({}, { ...formValues }, { [fieldName]: event.target.value }),
    );
  };

  const handleBrandChange = (fieldName: string) => (
    event: React.ChangeEvent<{
      name?: string;
      value?: any;
    }>,
    newValue: SagisConfig | null,
  ) =>
    setFormValues(
      Object.assign({}, { ...formValues }, { [fieldName]: newValue?.ItemKey }),
    );

  const handleSubmit = () => {
    const formConstraints = getFormContraints();
    const validator = validate(formValues, formConstraints);
    setFormValidator(validator);
    const hasInputhLengthError = isAllInputsLengthValid(inputsLengthErrorText);
    if (
      validateFormFields(validator) &&
      isRegistrationNumberValid &&
      hasInputhLengthError
    ) {
      setClaimToPreopen(
        Object.assign(
          {},
          { ...claimToPreopen },
          {
            opposingParty: {
              hasMaterialDammage,
              vehicules,
              newVehicles: newVehicules,
            },
          },
        ),
      );
      if (props.setClaimRecap) {
        props.setClaimRecap(
          Object.assign(
            {},
            { ...claimToPreopen },
            {
              opposingParty: {
                hasMaterialDammage,
                vehicules,
                newVehicles: newVehicules,
              },
            },
          ),
        );
      }
      let updatedFormValues = formValues;
      if (featureService.hasFeature(FEATURES.RNVA)) {
        updatedFormValues = {
          ...formValues,
          [VEHICULE.REGISTRATION_NUMBER]: formatRegistrationNumber(
            formValues[VEHICULE.REGISTRATION_NUMBER],
            formValues[VEHICULE.REGISTRATION_NUMBER_TYPE],
          ),
        };
      }
      if (props.setOpposingPartyData) {
        props.setOpposingPartyData([
          ...props.opposingPartyData,
          { ...updatedFormValues, isDeleted: false },
        ]);
        setShowForm(false);
        setFormValues({
          [VEHICULE.BRAND]: null,
          [VEHICULE.REGISTRATION_NUMBER]: null,
          [VEHICULE.OPPOSING_PARTY]: null,
          [VEHICULE.FIRST_NAME]: null,
          [VEHICULE.LAST_NAME]: null,
          [VEHICULE.REGISTRATION_NUMBER_TYPE]: null,
          [VEHICULE.RESPONSABILITY]: currentResponsability
            ? currentResponsability.nearestResponsabilityKey
            : null,
          [VEHICULE.RESPONSABILITY_RATE]: currentResponsability
            ? currentResponsability.nearestResponsabilityRate
            : null,
        });
      }
      setToggleOpen(true);
    }
  };

  const translateArrayErrorMessages = arrayTranslate(props.intl.formatMessage);

  const { message } = displayErrorText(
    formValidator,
    translateArrayErrorMessages(formValidator, true),
  );
  return (
    <>
      {!showForm && renderSubForm ? (
        <EmptyAddFormBloc
          title={translate(messages.addOpposingParty, props)}
          buttonBlocTitle={translate(messages.add, props)}
          icon={<AddCircleOutlineIcon className="icon" fontSize="small" />}
          handleClick={() => {
            setShowForm(true);
          }}
        />
      ) : (
        <>
          <FormContainer>
            <Grid container direction={'row'} spacing={2}>
              <Grid item xs={isForDrawer ? 12 : 2}>
                <Autocomplete
                  id="standard-basic-xx"
                  data-testid="autocomplete"
                  className="formEl"
                  options={get(listings, 'vehicleBrands', [])}
                  getOptionLabel={getOptions}
                  value={brand(listings, formValues)}
                  autoSelect={true}
                  onChange={handleBrandChange(VEHICULE.BRAND)}
                  filterOptions={filterOptions}
                  renderInput={params => (
                    <TextField
                      {...params}
                      name={VEHICULE.BRAND}
                      label={translate(
                        { ...messages.claimInformationVehicleBrand },
                        props,
                      )}
                      error={!!get(formValidator, VEHICULE.BRAND)}
                      helperText={message[VEHICULE.BRAND]}
                    />
                  )}
                />
              </Grid>
              {featureService.hasFeature(FEATURES.RNVA) && (
                <Grid item xs={isForDrawer ? 12 : 3}>
                  <FormControl className="formEl">
                    <InputLabel>
                      {translate({ ...messages.registrationType }, props)}
                    </InputLabel>
                    <Select
                      onChange={handleFormChange(
                        VEHICULE.REGISTRATION_NUMBER_TYPE,
                      )}
                      name={VEHICULE.REGISTRATION_NUMBER_TYPE}
                      value={get(
                        formValues,
                        [VEHICULE.REGISTRATION_NUMBER_TYPE],
                        '',
                      )}
                    >
                      {!isEmpty(get(listings, 'registrationNumberTypes', [])) &&
                        get(listings, 'registrationNumberTypes', []).map(
                          (el: any) => (
                            <MenuItem value={el.id} key={el.title}>
                              {el.title}
                            </MenuItem>
                          ),
                        )}
                    </Select>
                    {!!get(
                      formValidator,
                      VEHICULE.REGISTRATION_NUMBER_TYPE,
                    ) && (
                      <FormHelperText className="error">
                        {message[VEHICULE.REGISTRATION_NUMBER_TYPE]}
                      </FormHelperText>
                    )}
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={isForDrawer ? 12 : 2}>
                <TextField
                  label={translate({ ...messages.registration }, props)}
                  className="formEl"
                  placeholder={
                    featureService.hasFeature(FEATURES.RNVA)
                      ? getByPlaceholderText(
                          formValues[VEHICULE.REGISTRATION_NUMBER_TYPE],
                        )
                      : ''
                  }
                  onChange={handleFormChange(VEHICULE.REGISTRATION_NUMBER)}
                  name={VEHICULE.REGISTRATION_NUMBER}
                  value={get(formValues, [VEHICULE.REGISTRATION_NUMBER])}
                  error={
                    !!get(formValidator, VEHICULE.REGISTRATION_NUMBER) ||
                    (!featureService.hasFeature(FEATURES.RNVA) &&
                      !isRegistrationNumberValid &&
                      translate(messages.registrationNumberAlert, props)) ||
                    (featureService.hasFeature(FEATURES.RNVA) &&
                      !!registrationNumberErrorText)
                  }
                  helperText={
                    message[VEHICULE.REGISTRATION_NUMBER] ||
                    (!featureService.hasFeature(FEATURES.RNVA) &&
                      !isRegistrationNumberValid &&
                      translate(messages.registrationNumberAlert, props)) ||
                    (featureService.hasFeature(FEATURES.RNVA) &&
                      registrationNumberErrorText)
                  }
                  InputProps={{ inputProps: { maxlength: 20 } }}
                />
              </Grid>
              <Grid item xs={isForDrawer ? 12 : 3}>
                <FormControl className="formEl">
                  <InputLabel>
                    {translate({ ...messages.opposingCompanies }, props)}
                  </InputLabel>
                  <Select
                    onChange={handleFormChange(VEHICULE.OPPOSING_PARTY)}
                    name={VEHICULE.OPPOSING_PARTY}
                    value={get(formValues, [VEHICULE.OPPOSING_PARTY], '')}
                  >
                    {!isEmpty(get(listings, 'compagnies', [])) &&
                      filtredCompanies(
                        listings!.compagnies,
                        listings!.insuranceCompanies,
                      ).map(company => (
                        <MenuItem value={company.ItemKey} key={company.ItemKey}>
                          {company.ItemDescription}
                        </MenuItem>
                      ))}
                  </Select>
                  {!!get(formValidator, VEHICULE.OPPOSING_PARTY) && (
                    <FormHelperText className="error">
                      {message[VEHICULE.OPPOSING_PARTY]}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={isForDrawer ? 12 : 2}>
                <TextField
                  label={translate({ ...messages.opposingFirstName }, props)}
                  className="formEl"
                  onChange={handleFormChange(VEHICULE.FIRST_NAME)}
                  name={VEHICULE.FIRST_NAME}
                  value={get(formValues, [VEHICULE.FIRST_NAME])}
                  error={!!get(formValidator, VEHICULE.FIRST_NAME)}
                  helperText={message[VEHICULE.FIRST_NAME]}
                />
              </Grid>
              <Grid item xs={isForDrawer ? 12 : 2}>
                <TextField
                  label={translate({ ...messages.opposingLastName }, props)}
                  className="formEl"
                  onChange={handleFormChange(VEHICULE.LAST_NAME)}
                  name={VEHICULE.LAST_NAME}
                  value={get(formValues, [VEHICULE.LAST_NAME])}
                  error={!!get(formValidator, VEHICULE.LAST_NAME)}
                  helperText={message[VEHICULE.LAST_NAME]}
                />
              </Grid>
              {featureService.hasFeature(FEATURES.RNVA) && (
                <>
                  <Grid item xs={isForDrawer ? 12 : 2}>
                    <TextField
                      label={translate(
                        { ...messages.claimDeclarationPoliceNumber },
                        props,
                      )}
                      className="formEl"
                      onChange={handleFormChange(
                        VEHICULE.POLICY_NUMBER_OPPOSING,
                      )}
                      name={VEHICULE.POLICY_NUMBER_OPPOSING}
                      value={get(formValues, [VEHICULE.POLICY_NUMBER_OPPOSING])}
                      error={
                        !!get(formValidator, VEHICULE.POLICY_NUMBER_OPPOSING) ||
                        !!get(inputsLengthErrorText, [
                          VEHICULE.POLICY_NUMBER_OPPOSING,
                        ])
                      }
                      helperText={
                        message[VEHICULE.POLICY_NUMBER_OPPOSING] ||
                        get(inputsLengthErrorText, [
                          VEHICULE.POLICY_NUMBER_OPPOSING,
                        ])
                      }
                    />
                  </Grid>
                  <Grid item xs={isForDrawer ? 12 : 2}>
                    <TextField
                      label={translate(
                        { ...messages.claimDeclarationCertificateNumber },
                        props,
                      )}
                      className="formEl"
                      onChange={handleFormChange(
                        VEHICULE.CERTIFICATE_NUMBER_OPPOSING,
                      )}
                      name={VEHICULE.CERTIFICATE_NUMBER_OPPOSING}
                      value={get(formValues, [
                        VEHICULE.CERTIFICATE_NUMBER_OPPOSING,
                      ])}
                      error={
                        !!get(
                          formValidator,
                          VEHICULE.CERTIFICATE_NUMBER_OPPOSING,
                        ) ||
                        !!get(inputsLengthErrorText, [
                          VEHICULE.CERTIFICATE_NUMBER_OPPOSING,
                        ])
                      }
                      helperText={
                        message[VEHICULE.CERTIFICATE_NUMBER_OPPOSING] ||
                        get(inputsLengthErrorText, [
                          VEHICULE.CERTIFICATE_NUMBER_OPPOSING,
                        ])
                      }
                    />
                  </Grid>
                  <Grid item xs={isForDrawer ? 12 : 2}>
                    <TextField
                      label={translate(
                        { ...messages.claimDeclarationClaimNumber },
                        props,
                      )}
                      className="formEl"
                      onChange={handleFormChange(
                        VEHICULE.CLAIM_NUMBER_OPPOSING,
                      )}
                      name={VEHICULE.CLAIM_NUMBER_OPPOSING}
                      value={get(formValues, [VEHICULE.CLAIM_NUMBER_OPPOSING])}
                      error={
                        !!get(formValidator, VEHICULE.CLAIM_NUMBER_OPPOSING) ||
                        !!get(inputsLengthErrorText, [
                          VEHICULE.CLAIM_NUMBER_OPPOSING,
                        ])
                      }
                      helperText={
                        message[VEHICULE.CLAIM_NUMBER_OPPOSING] ||
                        get(inputsLengthErrorText, [
                          VEHICULE.CLAIM_NUMBER_OPPOSING,
                        ])
                      }
                    />
                  </Grid>
                  <Grid item xs={isForDrawer ? 12 : 3}>
                    <FormControl className="formEl">
                      <InputLabel>
                        {translate({ ...messages.responsability }, props)}
                      </InputLabel>
                      <Select
                        data-testid="responsability-select"
                        onChange={handleFormChange(VEHICULE.RESPONSABILITY)}
                        name={VEHICULE.RESPONSABILITY}
                        value={get(formValues, [VEHICULE.RESPONSABILITY], '')}
                      >
                        {!isEmpty(get(listings, 'responsability', [])) &&
                          get(listings, 'responsability', []).map((el: any) => (
                            <MenuItem
                              value={el.ItemKey}
                              key={el.ItemDescription}
                            >
                              {el.ItemDescription}
                            </MenuItem>
                          ))}
                      </Select>
                      {!!get(formValidator, VEHICULE.RESPONSABILITY) && (
                        <FormHelperText className="error">
                          {message[VEHICULE.RESPONSABILITY]}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                </>
              )}
            </Grid>
          </FormContainer>
          <FormSubmitWrapper>
            <section className="submit-add-form">
              <Grid container direction="row">
                <Grid item xs={isForDrawer ? 4 : 2}>
                  <PrimaryButton
                    onClick={handleSubmit}
                    title={translate(messages.submitForm, props)}
                  />
                </Grid>
                <Grid item xs={isForDrawer ? 4 : 2}>
                  <SecondaryButton
                    onClick={handleCancel}
                    title={translate(messages.cancelForm, props)}
                  />
                </Grid>
              </Grid>
            </section>
          </FormSubmitWrapper>
        </>
      )}
    </>
  );
};

export default injectIntl(AddOpposingParty);
